import React, { useState } from 'react';
import { Device } from '../../lib/types';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

function formatAge(months: number): string {
  if (months < 12) {
    return `${months} month${months === 1 ? '' : 's'}`;
  }
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (m === 0) {
    return `${y} year${y === 1 ? '' : 's'}`;
  }
  return `${y} yr${y === 1 ? '' : 's'} ${m} mo${m === 1 ? '' : 's'}`;
}

interface AddPetModalProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
  onAddPet: (deviceId: string, petName: string) => void;
}

export default function AddPetModal({ open, onClose, device, onAddPet }: AddPetModalProps) {
  const [name, setName] = useState('');
  const [petType, setPetType] = useState('Dog');
  const [breed, setBreed] = useState('');
  const [ageMonths, setAgeMonths] = useState(12);

  if (!device) {
    return null;
  }

  const currentDevice = device;

  function handleClose() {
    setName('');
    setPetType('Dog');
    setBreed('');
    setAgeMonths(12);
    onClose();
  }

  function handleConfirm() {
    if (!name.trim()) {
      return;
    }
    onAddPet(currentDevice.id, name.trim());
    handleClose();
  }

  function decrementAge() {
    setAgeMonths((prev) => {
      if (prev <= 1) {
        return 1;
      }
      return prev <= 12 ? prev - 1 : prev - 6;
    });
  }

  function incrementAge() {
    setAgeMonths((prev) => {
      const next = prev < 12 ? prev + 1 : prev + 6;
      return Math.min(next, 240);
    });
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle>Add pet</ModalTitle>
      <p className={styles.modalDescription}>
        Add a pet to <strong>{currentDevice.id}</strong>.
      </p>

      <PortalField label="Name">
        <PortalInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Pepper" />
      </PortalField>

      <PortalField label="Type">
        <ToggleGroup
          options={[
            { value: 'Dog', label: 'Dog' },
            { value: 'Cat', label: 'Cat' },
          ]}
          value={petType}
          onChange={setPetType}
        />
      </PortalField>

      <PortalField label="Breed">
        <PortalInput value={breed} onChange={(e) => setBreed(e.target.value)} placeholder="e.g. Golden Retriever" />
      </PortalField>

      <PortalField label="Age">
        <div className={styles.ageStepper}>
          <Button variant="ghost" size="sm" onClick={decrementAge} style={{ width: 36, padding: 0 }}>
            &minus;
          </Button>
          <span className={styles.ageValue}>{formatAge(ageMonths)}</span>
          <Button variant="ghost" size="sm" onClick={incrementAge} style={{ width: 36, padding: 0 }}>
            +
          </Button>
        </div>
      </PortalField>

      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleConfirm}>
          Add Pet
        </Button>
      </ModalActions>
    </Modal>
  );
}
